import { jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getSelection, $isRangeSelection, COMMAND_PRIORITY_LOW, FORMAT_TEXT_COMMAND, SELECTION_CHANGE_COMMAND } from "lexical";
import { mergeRegister } from "@lexical/utils";
import { Box } from "../../../../Box/index.js";
import { Icon } from "../../../../Icon/index.js";
import { ToolbarButton } from "../common/index.js";
const strikethroughTextListener = (event, editor)=>{
    editor.update(()=>{
        const { key, ctrlKey, metaKey, shiftKey } = event;
        if ('x' === key.toLowerCase() && (ctrlKey || metaKey) && shiftKey) {
            event.preventDefault();
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
        }
    });
};
const FormatTextPlugin = ({ bold, italic, underline, strikethrough, code, superscript, subscript })=>{
    const [editor] = useLexicalComposerContext();
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isStrikethrough, setIsStrikethrough] = useState(false);
    const [isCode, setIsCode] = useState(false);
    const [isSuperscript, setIsSuperscript] = useState(false);
    const [isSubscript, setIsSubscript] = useState(false);
    const getFormatColor = (value)=>value ? 'original.brand.primary' : 'text.light.primary';
    const handleFormatClick = (format)=>()=>{
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
        };
    const updateToolbar = useCallback(()=>{
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
            setIsBold(selection.hasFormat('bold'));
            setIsItalic(selection.hasFormat('italic'));
            setIsUnderline(selection.hasFormat('underline'));
            setIsStrikethrough(selection.hasFormat('strikethrough'));
            setIsCode(selection.hasFormat('code'));
            setIsSuperscript(selection.hasFormat("superscript"));
            setIsSubscript(selection.hasFormat("subscript"));
        }
    }, [
        editor
    ]);
    useEffect(()=>mergeRegister(editor.registerUpdateListener(({ editorState })=>{
            editorState.read(()=>{
                updateToolbar();
            });
        }), editor.registerCommand(SELECTION_CHANGE_COMMAND, ()=>{
            updateToolbar();
            return false;
        }, COMMAND_PRIORITY_LOW), editor.registerCommand(FORMAT_TEXT_COMMAND, (payload)=>{
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                if ('underline' === payload && selection.hasFormat('strikethrough')) selection.formatText('strikethrough', void 0);
                if ('strikethrough' === payload && selection.hasFormat('underline')) selection.formatText('underline', void 0);
                if ('code' === payload) {
                    if (selection.hasFormat('bold')) selection.formatText('bold', void 0);
                    if (selection.hasFormat('italic')) selection.formatText('italic', void 0);
                    if (selection.hasFormat('underline')) selection.formatText('underline', void 0);
                    if (selection.hasFormat('strikethrough')) selection.formatText('strikethrough', void 0);
                    if (selection.hasFormat("superscript")) selection.formatText("superscript", void 0);
                    if (selection.hasFormat("subscript")) selection.formatText("subscript", void 0);
                }
                if ('code' !== payload && selection.hasFormat('code')) selection.formatText('code', void 0);
                selection.formatText(payload);
            }
            return true;
        }, 1)), [
        editor,
        updateToolbar
    ]);
    useEffect(()=>{
        const removeRootListener = editor.registerRootListener((rootElement, prevRootElement)=>{
            null == rootElement || rootElement.addEventListener('keydown', (e)=>strikethroughTextListener(e, editor));
            null == prevRootElement || prevRootElement.removeEventListener('keydown', (e)=>strikethroughTextListener(e, editor));
        });
        return ()=>{
            removeRootListener();
        };
    }, [
        editor
    ]);
    return /*#__PURE__*/ jsxs(Box, {
        display: "flex",
        gap: "2",
        children: [
            bold && /*#__PURE__*/ jsx(ToolbarButton, {
                tooltip: "Жирный текст",
                onClick: handleFormatClick('bold'),
                children: /*#__PURE__*/ jsx(Icon, {
                    name: "text_bold_outline",
                    size: "16",
                    color: getFormatColor(isBold)
                })
            }),
            italic && /*#__PURE__*/ jsx(ToolbarButton, {
                tooltip: "Наклонный текст",
                onClick: handleFormatClick('italic'),
                children: /*#__PURE__*/ jsx(Icon, {
                    name: "text_italic_outline",
                    size: "16",
                    color: getFormatColor(isItalic)
                })
            }),
            underline && /*#__PURE__*/ jsx(ToolbarButton, {
                tooltip: "Подчеркнутый текст",
                onClick: handleFormatClick('underline'),
                children: /*#__PURE__*/ jsx(Icon, {
                    name: "text_underline_outline",
                    size: "16",
                    color: getFormatColor(isUnderline)
                })
            }),
            strikethrough && /*#__PURE__*/ jsx(ToolbarButton, {
                tooltip: "Зачеркнутый текст",
                onClick: handleFormatClick('strikethrough'),
                children: /*#__PURE__*/ jsx(Icon, {
                    name: "text_strikethrough_outline",
                    size: "16",
                    color: getFormatColor(isStrikethrough)
                })
            }),
            code && /*#__PURE__*/ jsx(ToolbarButton, {
                tooltip: "Исходный код",
                onClick: handleFormatClick('code'),
                children: /*#__PURE__*/ jsx(Icon, {
                    name: "vkhrtek_code",
                    color: getFormatColor(isCode),
                    size: "16"
                })
            }),
            superscript && /*#__PURE__*/ jsx(ToolbarButton, {
                tooltip: "Надстрочный текст",
                onClick: handleFormatClick("superscript"),
                children: /*#__PURE__*/ jsx(Icon, {
                    name: "vkhrtek_superscript",
                    color: getFormatColor(isSuperscript),
                    size: "16"
                })
            }),
            subscript && /*#__PURE__*/ jsx(ToolbarButton, {
                tooltip: "Подстрочный текст",
                onClick: handleFormatClick("subscript"),
                children: /*#__PURE__*/ jsx(Icon, {
                    name: "vkhrtek_subscript",
                    color: getFormatColor(isSubscript),
                    size: "16"
                })
            })
        ]
    });
};
export { FormatTextPlugin };
